import React, { useMemo } from 'react';
import { AppTable, Button } from '@allygpo/ally-ux';
import { getCoreRowModel, useReactTable, type ColumnDef, type Table } from '@tanstack/react-table';
import { Checkbox, tableView, unpaginated } from '../../components/ui';
import type { PayerMixItem, ProductFamily } from '../../types';

type Matrix = { [drugId: string]: { [payer: string]: boolean } };

/** What the matrix cells read from `meta.view`. */
interface MatrixView {
  matrix: Matrix;
  /** Families left out of the mix are greyed out and can't be edited. */
  isIncluded: (family: ProductFamily) => boolean;
  ready: boolean;
  showEffectiveShare: boolean;
  allSelected: boolean;
  /** At least one payer is eligible somewhere (the matrix "All" box shows mixed when not all are). */
  anySelected: boolean;
  onToggleAll: (next: boolean) => void;
  onToggleRow: (productFamilyId: string, next: boolean) => void;
  onToggleCell: (productFamilyId: string, payer: string, next: boolean) => void;
  hasGrouping: (payer: string) => boolean;
  onJumpToGrouping: (payer: string) => void;
  payers: PayerMixItem[];
}

const view = (table: Table<ProductFamily>) => tableView<MatrixView, ProductFamily>(table);
const idOf = (family: ProductFamily) => String(family.product_family_id);
const eligible = (v: MatrixView, family: ProductFamily, payer: string) => v.matrix[idOf(family)]?.[payer] ?? true;
/** Share of patients the family can reach: 100% minus the payers it's not eligible for. */
const maxShareOf = (v: MatrixView, family: ProductFamily) =>
  Math.max(0, Math.min(100, 100 - v.payers.reduce((sum, item) => (eligible(v, family, item.payer) ? sum : sum + item.percent), 0)));
const CENTER = { cellStyle: { textAlign: 'center' as const }, headerStyle: { textAlign: 'center' as const } };

const leadColumns: ColumnDef<ProductFamily>[] = [
  {
    id: 'share',
    size: 64,
    header: 'Max market share',
    meta: { headerClassName: 'white-space-normal', cellClassName: 'font-semibold' },
    cell: ({ row: { original: family }, table }) => `${(Math.round(maxShareOf(view(table), family) * 10) / 10).toFixed(1)}%`,
  },
  {
    id: 'all',
    size: 44,
    header: ({ table }) => {
      const v = view(table);
      return (
        <span className="flex flex-column align-items-center u-gap-1 w-full">
          All
          <Checkbox checked={v.ready && v.allSelected} indeterminate={v.ready && !v.allSelected && v.anySelected} disabled={!v.ready} onChange={(next) => v.ready && v.onToggleAll(next)} ariaLabel="Select all payers for every product family" />
        </span>
      );
    },
    meta: CENTER,
    cell: ({ row: { original: family }, table }) => {
      const v = view(table);
      const all = v.payers.every((item) => eligible(v, family, item.payer));
      const some = !all && v.payers.some((item) => eligible(v, family, item.payer));
      return (
        <Checkbox
          checked={v.ready && all}
          indeterminate={v.ready && some}
          disabled={!v.isIncluded(family) || !v.ready}
          onChange={(next) => v.ready && v.onToggleRow(idOf(family), next)}
          ariaLabel={`All payers for ${family.product_family_name}`}
        />
      );
    },
  },
  {
    id: 'family',
    size: 112,
    header: 'Product family',
    meta: { cellClassName: 'font-medium' },
    cell: ({ row: { original: family } }) => family.product_family_name.trim(),
  },
];

const payerColumn = (item: PayerMixItem): ColumnDef<ProductFamily> => ({
  id: `payer:${item.payer}`,
  size: 68,
  meta: { ...CENTER, headerClassName: 'white-space-normal' },
  header: ({ table }) => {
    const v = view(table);
    const linked = v.hasGrouping(item.payer);
    return (
      <span className="flex flex-column align-items-center u-gap-1 w-full text-2xs">
        <span className="text-secondary font-normal text-nowrap">{item.percent.toFixed(1)}%</span>
        <Button
          variant="link"
          className="payer-grouping-link text-2xs white-space-normal"
          onClick={() => linked && v.onJumpToGrouping(item.payer)}
          disabled={!linked}
          data-pr-tooltip={linked ? 'Jump to payer grouping' : 'No payer grouping available'}
          data-pr-position="top"
          lang="en"
        >
          {item.payer}
        </Button>
      </span>
    );
  },
  cell: ({ row: { original: family }, table }) => {
    const v = view(table);
    const checked = eligible(v, family, item.payer);
    const maxShare = maxShareOf(v, family);
    return (
      <span className="flex flex-column align-items-center">
        <Checkbox
          checked={v.ready && checked}
          disabled={!v.isIncluded(family) || !v.ready}
          ariaLabel={`${family.product_family_name} eligible for ${item.payer}`}
          onChange={(next) => v.ready && v.onToggleCell(idOf(family), item.payer, next)}
        />
        {v.showEffectiveShare && v.ready && checked && maxShare > 0 && (
          <span className="text-2xs text-secondary">{((item.percent / maxShare) * 100).toFixed(1)}%</span>
        )}
      </span>
    );
  },
});

type EligibilityMatrixProps = MatrixView & { families: ProductFamily[] };

/**
 * New run › Payer eligibility: which payers each product family can be assigned to. The share, All and
 * family columns stay put while the payer columns scroll sideways.
 */
const EligibilityMatrix: React.FC<EligibilityMatrixProps> = ({ families, ...matrixView }) => {
  const { payers } = matrixView;
  const columns = useMemo(() => [...leadColumns, ...payers.map(payerColumn)], [payers]);
  const table = useReactTable({
    ...unpaginated,
    data: families,
    columns,
    meta: { view: matrixView },
    state: { columnPinning: { left: ['share', 'all', 'family'] } },
    getRowId: idOf,
    getCoreRowModel: getCoreRowModel(),
  });
  return (
    <AppTable
      size="sm"
      className="elig-matrix-table"
      ariaLabel="Payer eligibility matrix"
      table={table}
      rowClassName={({ rowData }) => (matrixView.isIncluded(rowData) ? '' : 'text-tertiary')}
    />
  );
};

export default EligibilityMatrix;
